import { NgOptimizedImage } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { injectTranslate, TranslatePipe } from '@core/i18n-legacy';
import { SkillsMatrixXlsxService } from '@core/tellent-skills-logic';
import type { SkillsIdeateResultJson, SkillsIdeateFlowData } from '@core/tellent-skills-types';
import {
  ButtonComponent,
  ButtonLoadingDirective,
  DialogBodyComponent,
  DialogComponentI,
  DialogFooterComponent,
  DialogHeaderComponent,
  DialogRef,
  HeadingComponent,
  IconComponent,
  NotificationsController,
} from '@core/user-interface-legacy';

@Component({
  selector: 'tlc-skills-ideate-result-dialog',
  templateUrl: './skills-ideate-result-dialog.component.html',
  styleUrl: './skills-ideate-result-dialog.component.less',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ButtonComponent,
    ButtonLoadingDirective,
    DialogBodyComponent,
    DialogFooterComponent,
    DialogHeaderComponent,
    HeadingComponent,
    IconComponent,
    NgOptimizedImage,
    TranslatePipe,
  ],
})
export class SkillsIdeateResultDialogComponent implements DialogComponentI<
  Partial<SkillsIdeateFlowData>,
  { back: true }
> {
  private readonly t = injectTranslate();
  private readonly dialogRef = inject(DialogRef);
  private readonly destroyRef = inject(DestroyRef);
  private readonly xlsxService = inject(SkillsMatrixXlsxService);
  private readonly notificationsController = inject(NotificationsController);

  protected readonly resultJson = signal<SkillsIdeateResultJson | null>(null);
  protected readonly isDownloading = signal(false);

  protected readonly skillsCount = computed(() => {
    const result = this.resultJson();
    return result ? this.xlsxService.countSkills(result) : 0;
  });

  public setDialogData(data: Partial<SkillsIdeateFlowData>, _triggerUpdate: boolean): void {
    if (data.resultJson) {
      this.resultJson.set(data.resultJson);
    }
  }

  public downloadFile(): void {
    const result = this.resultJson();
    if (!result) return;

    this.isDownloading.set(true);
    this.xlsxService
      .download(result)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => this.isDownloading.set(false),
        error: () => {
          this.isDownloading.set(false);
          this.notificationsController.error({
            title: this.t('tlc.common.errors.unknown_error'),
          });
        },
      });
  }

  public backToStart(): void {
    this.dialogRef.close({ back: true });
  }

  public close(): void {
    this.dialogRef.close();
  }
}
